import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import NewLead from '../leads/NewLead';
import { useDispatch, useSelector } from 'react-redux';
import {
  setActiveMenuItem,
  setActiveSubmenuItem,
  toggleGreyMenu,
  closeGreyMenu,
} from '../../store/slices/menuSlice';
import { logout } from '../../store/slices/authSlice';
import { fetchMenuColor, setMenuLayout } from '../../store/slices/usersSlice';
import { useThemeColors } from '../../hooks/useThemeColors';
import WideDialog from '../common/WideDialog';
import UsersTab from '../settings/UsersTab';
import CostGroupsTab from '../settings/CostGroupsTab';
import CostCodesTab from '../settings/CostCodesTab';
import AppSettingsTab from '../settings/AppSettingsTab';
import CategoriesTab from '../settings/CategoriesTab';
import CitiesTab from '../settings/CitiesTab';
import AppearanceTab from '../settings/AppearanceTab';
import BackupsTab from '../settings/BackupsTab';

/* -------------------------------------------------------------------------- */
/* Color helpers                                                              */
/* -------------------------------------------------------------------------- */
const hexToRgb = (hex) => {
  const h = (hex || '#2563eb').replace('#', '');
  if (h.length !== 6) return { r: 37, g: 99, b: 235 };
  return {
    r: parseInt(h.substr(0, 2), 16),
    g: parseInt(h.substr(2, 2), 16),
    b: parseInt(h.substr(4, 2), 16),
  };
};

const mix = (hex, target, amount) => {
  const a = hexToRgb(hex);
  const b = hexToRgb(target);
  const r = Math.round(a.r + (b.r - a.r) * amount);
  const g = Math.round(a.g + (b.g - a.g) * amount);
  const bl = Math.round(a.b + (b.b - a.b) * amount);
  return `rgb(${r}, ${g}, ${bl})`;
};

// Build a palette derived from the user's selected menu color
const buildPalette = (primaryHex) => {
  const safe = primaryHex || '#2563eb';
  return {
    // Background: very dark version of the brand color (mix with near-black)
    surface: mix(safe, '#0b0f1a', 0.85),
    surfaceSoft: mix(safe, '#0b0f1a', 0.78),
    surfaceActive: mix(safe, '#ffffff', 0.18),
    accent: safe,
    accentSoft: mix(safe, '#ffffff', 0.35),
    border: 'rgba(255,255,255,0.06)',
    textPrimary: '#ffffff',
    textMuted: 'rgba(241, 245, 249, 0.72)',
    textDim: 'rgba(203, 213, 225, 0.55)',
  };
};

/* -------------------------------------------------------------------------- */
/* Icons                                                                       */
/* -------------------------------------------------------------------------- */
const Icon = {
  Dashboard: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  Leads: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h8M8 13h8M8 17h5" />
    </svg>
  ),
  CheckCircle: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  ),
  Plus: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  ),
  Document: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
      <path d="M14 3v6h6M8 13h8M8 17h5" />
    </svg>
  ),
  MapPin: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s-7-7.5-7-12a7 7 0 1114 0c0 4.5-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  ),
  Projects: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
    </svg>
  ),
  Clients: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0113 0" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15 20a4.5 4.5 0 016.5-4" />
    </svg>
  ),
  Inquiries: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a8 8 0 01-11.6 7.16L4 20l.84-5.4A8 8 0 1121 12z" />
    </svg>
  ),
  Safety: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3v6c0 4.5-3.4 8.4-8 9.5-4.6-1.1-8-5-8-9.5V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Weather: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="8" r="3" />
      <path d="M14 18h5a3 3 0 100-6 4.5 4.5 0 00-8.6.5A3.5 3.5 0 1011 18h3z" />
    </svg>
  ),
  Roof: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12L12 3l9 9" />
      <path d="M5 10v10h14V10" />
    </svg>
  ),
  Calendar: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  ),
  AI: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 3l1.8 4.7L17.5 9l-4.7 1.8L11 15l-1.8-4.2L4.5 9l4.7-1.3L11 3z" />
      <path d="M18 14l.9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9L18 14z" />
    </svg>
  ),
  Palette: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3a9 9 0 100 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.4-.3-.4-.5-.9-.5-1.4 0-1.1.9-2 2-2h2a4 4 0 004-4 8 8 0 00-9-7.2z" />
    </svg>
  ),
  Settings: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
      <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
    </svg>
  ),
  Logout: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </svg>
  ),
  ChevronDown: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  ChevronRight: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  Logo: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 14l6-6 6 6" />
      <path d="M6 19l6-6 6 6" />
    </svg>
  ),
};

/* -------------------------------------------------------------------------- */
/* Sub-item (used in expanded sections)                                       */
/* -------------------------------------------------------------------------- */
const SubItem = ({ icon, label, active, onClick, to, accentColor, highlight }) => {
  const Wrapper = to ? Link : 'button';
  const wrapperProps = to ? { to } : { type: 'button' };

  if (active) {
    return (
      <Wrapper
        {...wrapperProps}
        onClick={onClick}
        className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg bg-white text-slate-900 shadow-sm font-medium text-[13px] transition-all"
      >
        <span
          className="flex items-center justify-center w-6 h-6 rounded-full text-white shrink-0"
          style={{ backgroundColor: accentColor }}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
        <span className="truncate">{label}</span>
      </Wrapper>
    );
  }

  if (highlight) {
    return (
      <Wrapper
        {...wrapperProps}
        onClick={onClick}
        className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-white hover:brightness-110 text-[13px] font-medium transition-all"
        style={{ backgroundColor: `${accentColor}cc` }}
      >
        <span
          className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20 text-white shrink-0"
        >
          {React.cloneElement(icon, { className: 'w-3.5 h-3.5' })}
        </span>
        <span className="truncate">{label}</span>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      {...wrapperProps}
      onClick={onClick}
      className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 text-[13px] transition-all"
    >
      <span className="flex items-center justify-center w-6 h-6 rounded-full ring-1 ring-white/10 text-slate-300 shrink-0">
        {React.cloneElement(icon, { className: 'w-3.5 h-3.5' })}
      </span>
      <span className="truncate">{label}</span>
    </Wrapper>
  );
};

/* -------------------------------------------------------------------------- */
/* Top-level item (button or link)                                             */
/* -------------------------------------------------------------------------- */
const TopItem = ({ icon, label, active, onClick, to, trailing, accentColor }) => {
  const Wrapper = to ? Link : 'button';
  const wrapperProps = to ? { to } : { type: 'button' };

  return (
    <Wrapper
      {...wrapperProps}
      onClick={onClick}
      className={`group flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all
        ${active
          ? 'text-white bg-white/10'
          : 'text-slate-300 hover:text-white hover:bg-white/5'
        }`}
    >
      <span
        className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors shrink-0
          ${active ? 'text-white' : 'text-slate-400 group-hover:text-white'}`}
        style={active ? { backgroundColor: `${accentColor}33` } : undefined}
      >
        {icon}
      </span>
      <span className="flex-1 text-left truncate">{label}</span>
      {trailing}
    </Wrapper>
  );
};

/* -------------------------------------------------------------------------- */
/* Section with expandable sub-items                                           */
/* -------------------------------------------------------------------------- */
const Section = ({ icon, label, expanded, onToggle, isActive, accentColor, children }) => {
  return (
    <div className="rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all
          ${isActive || expanded
            ? 'text-white bg-white/10'
            : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
      >
        <span
          className="flex items-center justify-center w-7 h-7 rounded-lg shrink-0"
          style={{
            backgroundColor: isActive || expanded ? `${accentColor}33` : 'transparent',
            color: isActive || expanded ? '#fff' : undefined,
          }}
        >
          {icon}
        </span>
        <span className="flex-1 text-left truncate">{label}</span>
        <span className={`transition-transform duration-200 shrink-0 ${expanded ? 'rotate-0' : '-rotate-90'}`}>
          {Icon.ChevronDown}
        </span>
      </button>

      <div
        className={`transition-[max-height,opacity] duration-300 ease-out overflow-hidden
          ${expanded ? 'max-h-[400px] opacity-100 mt-1' : 'max-h-0 opacity-0'}`}
      >
        <div className="mx-0.5 mb-1 p-1.5 rounded-xl bg-black/15 ring-1 ring-white/5 flex flex-col gap-0.5">
          {children}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Main Sidebar                                                                */
/* -------------------------------------------------------------------------- */
const BlueMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeMenuItem, activeSubmenuItem } = useSelector((state) => state.menu);
  const { user } = useSelector((state) => state.auth);
  const { buttonColors } = useThemeColors();

  const isAdmin = user && (user.profile?.role === 'admin' || user.profile?.role === 'super_admin');
  const accentColor = buttonColors?.primary || '#2563eb';
  const palette = useMemo(() => buildPalette(accentColor), [accentColor]);

  const [isNewLeadOpen, setIsNewLeadOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [isMaximized, setIsMaximized] = useState(true);

  // Section expansion (Leads, Projects)
  const [expandedSections, setExpandedSections] = useState({
    leads: true,
    projects: location.pathname.startsWith('/projects'),
  });

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef(null);

  useEffect(() => {
    if (!isProfileMenuOpen) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setIsProfileMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isProfileMenuOpen]);

  const settingsTabs = useMemo(
    () => [
      { id: 'users', name: 'Users' },
      { id: 'categories', name: 'Categories' },
      { id: 'costGroups', name: 'Cost Groups' },
      { id: 'costCodes', name: 'Cost Codes' },
      { id: 'cities', name: 'Cities' },
      { id: 'appSettings', name: 'App Settings' },
      { id: 'appearance', name: 'Appearance' },
      ...(isAdmin ? [{ id: 'backups', name: 'Backups' }] : []),
    ],
    [isAdmin]
  );

  useEffect(() => {
    dispatch(fetchMenuColor());
  }, [dispatch]);

  useEffect(() => {
    const path = location.pathname;
    if (path !== activeMenuItem) {
      dispatch(setActiveMenuItem(path));
    }

    // Auto-expand the section that contains the current path
    setExpandedSections((prev) => ({
      ...prev,
      leads: path.startsWith('/leads') || prev.leads,
      projects: (path.startsWith('/projects') && path !== '/projects/calendar') || prev.projects,
    }));

    // Keep legacy grey-menu redux in sync (other code still reads activeSubmenuItem)
    if (path.startsWith('/leads')) {
      dispatch(toggleGreyMenu('leads'));
      dispatch(setActiveSubmenuItem(path));
    } else if (path.startsWith('/projects') && path !== '/projects/calendar') {
      dispatch(toggleGreyMenu('projects'));
      dispatch(setActiveSubmenuItem(path));
    } else {
      dispatch(closeGreyMenu());
    }
  }, [location.pathname, dispatch, activeMenuItem]);

  const isActive = (path) => location.pathname === path;
  const isSubActive = (path) => activeSubmenuItem === path || location.pathname === path;

  const handleClick = (path) => {
    dispatch(setActiveMenuItem(path));
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className="fixed left-0 top-0 h-screen w-[240px] hidden md:flex flex-col z-30 text-white"
        style={{
          background: `linear-gradient(180deg, ${palette.surfaceSoft} 0%, ${palette.surface} 100%)`,
          borderRight: `1px solid ${palette.border}`,
        }}
      >
        {/* Brand */}
        <div className="px-4 pt-4 pb-3 shrink-0">
          <div className="flex items-center gap-3 px-1">
            <div
              className="flex items-center justify-center w-9 h-9 rounded-xl text-white shadow-md shrink-0"
              style={{ backgroundColor: accentColor }}
            >
              {Icon.Logo}
            </div>
            <div className="leading-tight min-w-0">
              <p className="text-sm font-bold tracking-wide text-white truncate">JRT DRIVER</p>
              <p className="text-[11px] text-slate-400 truncate">Project Management</p>
            </div>
          </div>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-1 custom-scrollbar-dark">
          {/* Dashboard (admin) */}
          {isAdmin && (
            <TopItem
              icon={Icon.Dashboard}
              label="Dashboard"
              active={isActive('/')}
              to="/"
              onClick={() => handleClick('/')}
              accentColor={accentColor}
            />
          )}

          {/* Leads & Estimates (expandable) */}
          <Section
            icon={Icon.Leads}
            label="Leads & Estimates"
            expanded={expandedSections.leads}
            onToggle={() => toggleSection('leads')}
            isActive={location.pathname.startsWith('/leads')}
            accentColor={accentColor}
          >
            <SubItem
              icon={Icon.CheckCircle}
              label="All Leads"
              to="/leads"
              active={location.pathname === '/leads'}
              onClick={() => handleClick('/leads')}
              accentColor={accentColor}
            />
            <SubItem
              icon={Icon.Plus}
              label="New Lead"
              active={false}
              highlight
              onClick={() => setIsNewLeadOpen(true)}
              accentColor={accentColor}
            />
            <SubItem
              icon={Icon.MapPin}
              label="Map View"
              to="/leads/map"
              active={location.pathname === '/leads/map'}
              onClick={() => handleClick('/leads/map')}
              accentColor={accentColor}
            />
          </Section>

          {/* Projects (expandable) */}
          <Section
            icon={Icon.Projects}
            label="Projects"
            expanded={expandedSections.projects}
            onToggle={() => toggleSection('projects')}
            isActive={location.pathname.startsWith('/projects') && location.pathname !== '/projects/calendar'}
            accentColor={accentColor}
          >
            <SubItem
              icon={Icon.Document}
              label="All Projects"
              to="/projects"
              active={isSubActive('/projects')}
              onClick={() => handleClick('/projects')}
              accentColor={accentColor}
            />
            <SubItem
              icon={Icon.MapPin}
              label="Map View"
              to="/projects/map"
              active={isSubActive('/projects/map')}
              onClick={() => handleClick('/projects/map')}
              accentColor={accentColor}
            />
          </Section>

          {/* Clients */}
          <TopItem
            icon={Icon.Clients}
            label="Clients"
            to="/clients"
            active={isActive('/clients')}
            onClick={() => handleClick('/clients')}
            accentColor={accentColor}
          />

          {/* Website Inquiries */}
          <TopItem
            icon={Icon.Inquiries}
            label="Website Inquiries"
            to="/inquiries"
            active={isActive('/inquiries')}
            onClick={() => handleClick('/inquiries')}
            accentColor={accentColor}
          />

          {/* Calendar */}
          <TopItem
            icon={Icon.Calendar}
            label="Calendar"
            to="/projects/calendar"
            active={isActive('/projects/calendar')}
            onClick={() => handleClick('/projects/calendar')}
            accentColor={accentColor}
          />

          {/* File Storage */}
          <TopItem
            icon={Icon.Safety}
            label="File Storage"
            to="/safety"
            active={isActive('/safety')}
            onClick={() => handleClick('/safety')}
            accentColor={accentColor}
          />

          {/* Weather & Map */}
          <TopItem
            icon={Icon.Weather}
            label="Weather & Map"
            to="/weather"
            active={isActive('/weather')}
            onClick={() => handleClick('/weather')}
            accentColor={accentColor}
          />

          {/* Roof Measurement */}
          <TopItem
            icon={Icon.Roof}
            label="Roof Measurement"
            to="/roof-measurement"
            active={isActive('/roof-measurement')}
            onClick={() => handleClick('/roof-measurement')}
            accentColor={accentColor}
          />

          {/* JRT GPT */}
          <TopItem
            icon={Icon.AI}
            label="JRT GPT"
            to="/ai"
            active={isActive('/ai')}
            onClick={() => handleClick('/ai')}
            accentColor={accentColor}
          />

          {/* Divider */}
          <div className="my-2 h-px bg-white/5" />

          {/* Color Theme */}
          <TopItem
            icon={Icon.Palette}
            label="Color Theme"
            onClick={() => {
              setIsSettingsOpen(true);
              setActiveTab('appearance');
            }}
            accentColor={accentColor}
          />

          {/* Settings */}
          <TopItem
            icon={Icon.Settings}
            label="Settings"
            onClick={() => setIsSettingsOpen(true)}
            accentColor={accentColor}
          />

          {/* Switch to Classic Menu */}
          <TopItem
            icon={(
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d="M3 9h18M9 4v16" />
              </svg>
            )}
            label="Classic Layout"
            onClick={() => dispatch(setMenuLayout('classic'))}
            accentColor={accentColor}
          />

          {/* Sign Out */}
          <TopItem
            icon={Icon.Logout}
            label="Sign Out"
            onClick={handleLogout}
            accentColor={accentColor}
          />
        </nav>

        {/* User Profile */}
        <div className="p-3 shrink-0 relative" ref={profileMenuRef}>
          {/* Popover menu */}
          {isProfileMenuOpen && (
            <div
              className="absolute left-2 right-2 bottom-full mb-3 rounded-2xl overflow-hidden z-50 animate-in"
              style={{
                backgroundColor: '#ffffff',
                boxShadow:
                  '0 20px 50px -12px rgba(0,0,0,0.35), 0 8px 16px -8px rgba(0,0,0,0.18), 0 0 0 1px rgba(15,23,42,0.05)',
              }}
            >
              {/* Header with subtle accent bar */}
              <div className="relative px-4 pt-4 pb-3">
                <div
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ background: `linear-gradient(90deg, ${accentColor} 0%, ${palette.accentSoft} 100%)` }}
                />
                <div className="flex items-center gap-3">
                  {user?.profile?.profilePicture ? (
                    <img
                      src={user.profile.profilePicture}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                      style={{ boxShadow: `0 0 0 2px white, 0 0 0 3px ${accentColor}` }}
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                      style={{
                        backgroundColor: accentColor,
                        boxShadow: `0 0 0 2px white, 0 0 0 3px ${accentColor}`,
                      }}
                    >
                      {(user?.profile?.firstName?.[0] || 'U') + (user?.profile?.lastName?.[0] || '')}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
                      {user?.profile?.firstName} {user?.profile?.lastName}
                    </p>
                    <p className="text-[11px] text-gray-500 truncate mt-0.5">{user?.email}</p>
                  </div>
                </div>
                {user?.profile?.role && (
                  <div className="mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider"
                       style={{ backgroundColor: `${accentColor}15`, color: accentColor }}>
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
                    </svg>
                    {user.profile.role.replace('_', ' ')}
                  </div>
                )}
              </div>

              <div className="h-px bg-gray-100 mx-3" />

              {/* Action items */}
              <div className="p-1.5">
                <button
                  type="button"
                  onClick={() => { setIsProfileMenuOpen(false); setIsSettingsOpen(true); }}
                  className="group w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <span className="w-7 h-7 rounded-md flex items-center justify-center bg-gray-100 group-hover:bg-white transition-colors shrink-0">
                    <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21a8 8 0 0116 0" />
                    </svg>
                  </span>
                  <span className="font-medium">Profile & Settings</span>
                </button>

                <button
                  type="button"
                  onClick={() => { setIsProfileMenuOpen(false); dispatch(setMenuLayout('classic')); }}
                  className="group w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <span className="w-7 h-7 rounded-md flex items-center justify-center bg-gray-100 group-hover:bg-white transition-colors shrink-0">
                    <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <path d="M3 9h18M9 4v16" />
                    </svg>
                  </span>
                  <span className="font-medium whitespace-nowrap">Classic Layout</span>
                </button>
              </div>

              <div className="h-px bg-gray-100 mx-3" />

              <div className="p-1.5">
                <button
                  type="button"
                  onClick={() => { setIsProfileMenuOpen(false); handleLogout(); }}
                  className="group w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                >
                  <span className="w-7 h-7 rounded-md flex items-center justify-center bg-red-50 group-hover:bg-white transition-colors shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                      <path d="M16 17l5-5-5-5" />
                      <path d="M21 12H9" />
                    </svg>
                  </span>
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsProfileMenuOpen((v) => !v)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors ring-1 ring-white/5"
          >
            {user?.profile?.profilePicture ? (
              <img
                src={user.profile.profilePicture}
                alt={`${user.profile.firstName} ${user.profile.lastName}`}
                className="w-9 h-9 rounded-full object-cover ring-2 ring-white/20"
              />
            ) : (
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold ring-2 ring-white/20"
                style={{ backgroundColor: accentColor }}
              >
                {(user?.profile?.firstName?.[0] || 'U') + (user?.profile?.lastName?.[0] || '')}
              </div>
            )}
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold text-white truncate">
                {user?.profile?.firstName} {user?.profile?.lastName}
              </p>
              <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
            </div>
            <span
              className={`text-slate-400 transition-transform duration-200 ${isProfileMenuOpen ? 'rotate-180' : ''}`}
            >
              {Icon.ChevronDown}
            </span>
          </button>
        </div>
      </aside>

      {/* Layout spacer for desktop */}
      <div className="hidden md:block w-[240px] flex-shrink-0" />

      {/* Mobile bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 md:hidden z-30 border-t"
        style={{ backgroundColor: palette.surface, borderColor: palette.border }}
      >
        <div className="flex justify-around items-center h-16 px-2">
          {isAdmin && (
            <Link
              to="/"
              className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/') ? 'text-white' : 'text-slate-400'}`}
              onClick={() => handleClick('/')}
            >
              {Icon.Dashboard}
              <span className="text-[10px] mt-1">Dashboard</span>
            </Link>
          )}
          <Link
            to="/leads"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${location.pathname.startsWith('/leads') ? 'text-white' : 'text-slate-400'}`}
            onClick={() => handleClick('/leads')}
          >
            {Icon.Leads}
            <span className="text-[10px] mt-1">Leads</span>
          </Link>
          <Link
            to="/clients"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/clients') ? 'text-white' : 'text-slate-400'}`}
            onClick={() => handleClick('/clients')}
          >
            {Icon.Clients}
            <span className="text-[10px] mt-1">Clients</span>
          </Link>
          <Link
            to="/projects"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${location.pathname.startsWith('/projects') && location.pathname !== '/projects/calendar' ? 'text-white' : 'text-slate-400'}`}
            onClick={() => handleClick('/projects')}
          >
            {Icon.Projects}
            <span className="text-[10px] mt-1">Projects</span>
          </Link>
          <Link
            to="/projects/calendar"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/projects/calendar') ? 'text-white' : 'text-slate-400'}`}
            onClick={() => handleClick('/projects/calendar')}
          >
            {Icon.Calendar}
            <span className="text-[10px] mt-1">Calendar</span>
          </Link>
          <Link
            to="/weather"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/weather') ? 'text-white' : 'text-slate-400'}`}
            onClick={() => handleClick('/weather')}
          >
            {Icon.Weather}
            <span className="text-[10px] mt-1">Weather</span>
          </Link>
        </div>
      </nav>

      {/* New Lead Dialog */}
      <NewLead isOpen={isNewLeadOpen} onClose={() => setIsNewLeadOpen(false)} />

      {/* Settings Dialog */}
      <WideDialog isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)}>
        <div className={`${isMaximized ? 'fixed inset-0 z-50 bg-white' : 'bg-white rounded-lg shadow-xl max-w-7xl mx-auto'}`}>
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800">Settings</h2>
            <div className="flex items-center space-x-2">
              <button
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                onClick={() => setIsMaximized(!isMaximized)}
                title={isMaximized ? 'Restore dialog size' : 'Maximize dialog'}
              >
                {isMaximized ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="px-6 pt-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {settingsTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6 overflow-y-auto" style={{ height: isMaximized ? 'calc(100vh - 130px)' : 'min(70vh, 640px)' }}>
            {activeTab === 'users' && <UsersTab isMaximized={isMaximized} />}
            {activeTab === 'categories' && <CategoriesTab />}
            {activeTab === 'costGroups' && <CostGroupsTab />}
            {activeTab === 'costCodes' && <CostCodesTab />}
            {activeTab === 'cities' && <CitiesTab />}
            {activeTab === 'appSettings' && <AppSettingsTab />}
            {activeTab === 'appearance' && <AppearanceTab />}
            {activeTab === 'backups' && <BackupsTab />}
          </div>
        </div>
      </WideDialog>
    </>
  );
};

export default BlueMenu;
